import axios from 'src/utils/baseAxios';
import workFixtures from 'src/mocks/mailorbit/work.json';
import conversationFixtures from 'src/mocks/mailorbit/conversation.json';
import contentFixtures from 'src/mocks/mailorbit/content.json';
import messagingFixtures from 'src/mocks/mailorbit/messaging.json';
import workflowFixtures from 'src/mocks/mailorbit/workflow.json';
import notesFixtures from 'src/mocks/mailorbit/notes.json';
import agentFixtures from 'src/mocks/mailorbit/agent.json';
import classificationFixtures from 'src/mocks/mailorbit/classification.json';

/**
 * MailOrbit agent API servis katmanı (contract/agent-api-usage.md, kontrat v1.0).
 *
 * - SR-019: MailOrbit'e DOĞRUDAN istek atılmaz. Tüm çağrılar Sirius'un mevcut axios istemcisi
 *   (baseAxios; REACT_APP_API_URL + Sirius oturum başlıkları/Okta token) ile SiriusApi'ye gider:
 *   `api/MailOrbitAgent/<yol>`; yol, MailOrbit'in `/api/v1/tenants/current/` sonrası kısmıdır.
 *   SiriusApi isteği MailOrbit'e iletir; X-Tenant-Id'yi SiriusApi koyar, istemci göndermez.
 * - Tenant kapalıysa SiriusApi 404 döner (UI zaten kapıdan gizli; bkz. mailorbitFeature.js).
 * - Yalnızca agent kapsamı. Supervisor/admin yolları (merge, split, reassign ...) burada yoktur.
 * - Mail gövdesi/eki Sirius'ta saklanmaz; her çağrıda anlık okunur.
 *
 * Yapılandırma (env):
 *   REACT_APP_MAILORBIT_MOCK          "true" ise ağ çağrısı yapılmaz, src/mocks/mailorbit/*.json döner
 *
 * SR-019 ile KALDIRILAN env değişkenleri: REACT_APP_MAILORBIT_API_URL,
 *   REACT_APP_MAILORBIT_TOKEN_SOURCE, REACT_APP_MAILORBIT_TENANT_MAP,
 *   REACT_APP_MAILORBIT_ACTIVE_TENANTS (aktiflik artık GET api/MailOrbitFeature ile gelir).
 */

// REACT_APP_API_URL zaten ".../api" ile biter; bu yüzden yol "/MailOrbitAgent/..." olur.
const BASE_PATH = '/MailOrbitAgent';

// ---------------------------------------------------------------------------
// Yapılandırma okuyucuları (çağrı anında okunur; testlerde env değiştirilebilir)
// ---------------------------------------------------------------------------

export const isMailOrbitMock = () => process.env.REACT_APP_MAILORBIT_MOCK === 'true';

const getLocale = () => {
  try {
    return localStorage.getItem('i18nextLng') || null;
  } catch (e) {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Hata modeli
// ---------------------------------------------------------------------------

/**
 * kind -> i18n anahtarı (namespace: "mailorbit").
 * 403 ve 404 bilinçli olarak aynı mesajı verir (API de ikisini ayırmaz).
 */
export const MAILORBIT_ERROR_KEYS = {
  unauthorized: 'error_unauthorized',
  noAccess: 'error_no_access',
  conflict: 'error_conflict',
  conversationClosed: 'error_conversation_closed',
  validation: 'error_validation',
  config: 'error_config',
  network: 'error_network',
  unknown: 'error_unknown',
};

const classify = (status, problem) => {
  if (status === 401) return 'unauthorized';
  if (status === 403 || status === 404) return 'noAccess';
  if (status === 409) {
    return problem && problem.type === 'conversation_closed' ? 'conversationClosed' : 'conflict';
  }
  if (status === 400 || status === 422) return 'validation';
  if (!status) return 'network';
  return 'unknown';
};

export const createMailOrbitError = (kind, status, problem, cause) => {
  const err = new Error(`MailOrbit ${kind}${status ? ` (${status})` : ''}`);
  err.isMailOrbitError = true;
  err.kind = kind;
  err.status = status || null;
  err.i18nKey = MAILORBIT_ERROR_KEYS[kind] || MAILORBIT_ERROR_KEYS.unknown;
  err.problem = problem || null; // ProblemDetails / ValidationProblem gövdesi (varsa)
  err.cause = cause;
  return err;
};

export const isMailOrbitError = e => !!(e && e.isMailOrbitError);

/** UI için: t(`mailorbit:${getMailOrbitErrorKey(e)}`) */
export const getMailOrbitErrorKey = e =>
  isMailOrbitError(e) ? e.i18nKey : MAILORBIT_ERROR_KEYS.unknown;

// 401 işleyicileri
// ---------------------------------------------------------------------------

const handlers = {
  // () => Promise<string|null>: yeni token döndürürse istek bir kez tekrarlanır.
  refreshToken: null,
  // 401 kalıcıysa çağrılır. Varsayılan: window event; oturumu SONLANDIRMAZ (oturum
  // sonlandırma Sirius'un kendi axios interceptor'ında).
  onUnauthorized: error => {
    if (typeof window !== 'undefined' && window.dispatchEvent && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('mailorbit:unauthorized', { detail: error }));
    }
  },
};

/** { refreshToken, onUnauthorized } işleyicilerini değiştirir. */
export const setMailOrbitAuthHandlers = next => {
  Object.assign(handlers, next || {});
};

// ---------------------------------------------------------------------------
// İstek (Sirius axios istemcisi üzerinden) ve hata eşleme
// ---------------------------------------------------------------------------

const mapError = async (error, retry) => {
  if (isMailOrbitError(error)) throw error;
  const response = error && error.response;
  const status = response ? response.status : null;
  const config = error && error.config;

  if (status === 401 && config && !config.__mailorbitRetried && handlers.refreshToken) {
    try {
      const fresh = await handlers.refreshToken();
      if (fresh) return retry();
    } catch (e) {
      // yenileme başarısız: aşağıda 401 olarak işlenir
    }
  }

  const mapped = createMailOrbitError(
    classify(status, response && response.data),
    status,
    response && response.data,
    error
  );
  if (status === 401 && handlers.onUnauthorized) handlers.onUnauthorized(mapped);
  throw mapped;
};

const send = config => {
  const locale = getLocale();
  const headers = { ...(config.headers || {}) };
  if (locale) headers['X-MailOrbit-Locale'] = locale;
  const full = { ...config, headers };
  return axios.request(full).catch(error => mapError(error, () => send({ ...config, __mailorbitRetried: true })));
};

// ---------------------------------------------------------------------------
// Mock
// ---------------------------------------------------------------------------

const FIXTURES = {
  work: workFixtures,
  conversation: conversationFixtures,
  content: contentFixtures,
  messaging: messagingFixtures,
  workflow: workflowFixtures,
  notes: notesFixtures,
  agent: agentFixtures,
  classification: classificationFixtures,
};

/** Fixture var mı? ("dosya.anahtar") */
const hasMockFixture = ref => {
  const [file, key] = ref.split('.');
  return !!FIXTURES[file] && Object.prototype.hasOwnProperty.call(FIXTURES[file], key);
};

const mockResponse = (ref, responseType) => {
  const [file, key] = ref.split('.');
  if (!hasMockFixture(ref)) {
    return Promise.reject(createMailOrbitError('unknown', null, null));
  }
  const fixture = FIXTURES[file][key];
  if (responseType === 'blob') {
    return Promise.resolve(
      new Blob([fixture.text], { type: fixture.contentType || 'application/octet-stream' })
    );
  }
  return Promise.resolve(fixture === null ? null : JSON.parse(JSON.stringify(fixture)));
};

// ---------------------------------------------------------------------------
// İstek yardımcısı
// ---------------------------------------------------------------------------

const enc = encodeURIComponent;

const call = ({ method = 'get', path, params, data, mock, responseType, headers }) => {
  if (isMailOrbitMock()) return mockResponse(mock, responseType);
  return send({ method, url: `${BASE_PATH}${path}`, params, data, responseType, headers })
    .then(res => (res.status === 204 ? null : res.data));
};

const conv = id => `/conversations/${enc(id)}`;
const msg = (cid, mid) => `${conv(cid)}/messages/${enc(mid)}`;

// ---------------------------------------------------------------------------
// 2.1 Listeler
// params: skip, take, sort ('priority'|'created'), direction ('asc'|'desc'), includeClosed
// ---------------------------------------------------------------------------

export const getMyWork = (params = {}) =>
  call({ path: '/me/work', params, mock: 'work.meWork' });

export const getMyQueues = () => call({ path: '/me/queues', mock: 'work.meQueues' });

// params: unclaimedOnly, includeTerminal, skip, take, sort, direction
export const getQueueWorkItems = (queueId, params = {}) =>
  call({ path: `/queues/${enc(queueId)}/work-items`, params, mock: 'work.queueWorkItems' });

// params: q, queueId, openOnly
export const searchConversations = (params = {}) =>
  call({ path: '/conversations/search', params, mock: 'work.search' });

export const getConversationByReference = reference =>
  call({ path: `/conversations/by-reference/${enc(reference)}`, mock: 'work.byReference' });

// Yeni (MO-009). Eşleşme yoksa 200 + { items: [] } (açık nokta: by-reference 404 döner).
export const getConversationsByExternalLink = (system, externalId) =>
  call({
    path: '/conversations/by-external-link',
    params: { system, externalId },
    mock: 'work.byExternalLink',
  });

// ---------------------------------------------------------------------------
// 2.2 Conversation
// ---------------------------------------------------------------------------

export const getConversation = (conversationId, { includeBodies = false } = {}) =>
  call({
    path: conv(conversationId),
    params: { includeBodies },
    mock: 'conversation.detail',
  });

// params: offset, limit
export const getSenderHistory = (conversationId, params = {}) =>
  call({
    path: `${conv(conversationId)}/sender-history`,
    params,
    mock: 'conversation.senderHistory',
  });

/** Ekran açıldığında BİR kez çağrılır (arka plan yenilemesinde değil). { statusChanged } döner. */
export const markConversationOpened = conversationId =>
  call({ method: 'post', path: `${conv(conversationId)}/opened`, mock: 'conversation.opened' });

export const getAssignmentHistory = conversationId =>
  call({
    path: `${conv(conversationId)}/assignment-history`,
    mock: 'conversation.assignmentHistory',
  });

// ---------------------------------------------------------------------------
// 2.3 Mail içeriği (Sirius'a kopyalanmaz; her görüntülemede okunur)
// ---------------------------------------------------------------------------

export const getMessageBody = (conversationId, messageId) =>
  call({ path: `${msg(conversationId, messageId)}/body`, mock: 'content.body' });

export const getMessageAttachments = (conversationId, messageId) =>
  call({ path: `${msg(conversationId, messageId)}/attachments`, mock: 'content.attachments' });

/** Ek içeriği Blob olarak döner. */
export const getAttachmentContent = (conversationId, messageId, attachmentId, download = false) =>
  call({
    path: `${msg(conversationId, messageId)}/attachments/${enc(attachmentId)}`,
    params: { download },
    responseType: 'blob',
    mock: 'content.attachmentContent',
  });

// ---------------------------------------------------------------------------
// 2.4 Cevap, şablon ve AI
// ---------------------------------------------------------------------------

/**
 * payload: { idempotencyKey (zorunlu), toRecipients, ccRecipients, subject, bodyText,
 *            attachments: [{ fileName, contentBase64 }] }
 * Reply-all ayrı endpoint değildir; alıcılar toRecipients/ccRecipients ile verilir.
 */
export const sendReply = (conversationId, messageId, payload) =>
  call({
    method: 'post',
    path: `${msg(conversationId, messageId)}/reply`,
    data: payload,
    headers: payload && payload.idempotencyKey ? { 'Idempotency-Key': payload.idempotencyKey } : undefined,
    mock: 'messaging.reply',
  });

export const getAttachmentPolicy = () =>
  call({ path: '/messaging/attachment-policy', mock: 'messaging.attachmentPolicy' });

/** { replyWithAi }: false ise AI/çeviri/özet düğmeleri gizlenir. */
export const getMessagingCapabilities = () =>
  call({ path: '/messaging/capabilities', mock: 'messaging.capabilities' });

export const getReplySuggestion = (conversationId, messageId) =>
  call({
    path: `${msg(conversationId, messageId)}/reply-suggestion`,
    mock: 'messaging.replySuggestion',
  });

export const translateMessage = (conversationId, messageId, targetLanguage) =>
  call({
    method: 'post',
    path: `${msg(conversationId, messageId)}/translate`,
    data: { targetLanguage },
    mock: 'messaging.translate',
  });

export const summarizeConversation = conversationId =>
  call({ method: 'post', path: `${conv(conversationId)}/summary`, mock: 'messaging.summary' });

export const getOutlookDraft = (conversationId, messageId) =>
  call({
    path: `${msg(conversationId, messageId)}/outlook-draft`,
    mock: 'messaging.outlookDraft',
  });

export const saveOutlookDraft = (conversationId, messageId, payload) =>
  call({
    method: 'put',
    path: `${msg(conversationId, messageId)}/outlook-draft`,
    data: payload,
    mock: 'messaging.outlookDraft',
  });

export const deleteOutlookDraft = (conversationId, messageId) =>
  call({
    method: 'delete',
    path: `${msg(conversationId, messageId)}/outlook-draft`,
    mock: 'messaging.outlookDraft',
  });

export const getEmailTemplates = () =>
  call({ path: '/reference/email-templates', mock: 'messaging.templates' });

export const renderTemplate = (conversationId, messageId, templateId) =>
  call({
    method: 'post',
    path: `${msg(conversationId, messageId)}/render-template`,
    data: { templateId },
    mock: 'messaging.renderTemplate',
  });

/** Eşzamanlı cevap uyarısı: başka agent'ların taslak/cevap durumu. */
export const getReplyActivity = conversationId =>
  call({ path: `${conv(conversationId)}/reply-activity`, mock: 'messaging.replyActivity' });

/** "Bu agent cevap yazıyor" heartbeat'i; ekran yazma sırasında düzenli aralıkla çağrılır. */
export const sendReplyDraftHeartbeat = conversationId =>
  call({
    method: 'put',
    path: `${conv(conversationId)}/reply-draft`,
    mock: 'messaging.replyDraftHeartbeat',
  });

export const clearReplyDraft = conversationId =>
  call({
    method: 'delete',
    path: `${conv(conversationId)}/reply-draft`,
    mock: 'messaging.replyDraftClear',
  });

// ---------------------------------------------------------------------------
// 2.5 İş akışı (başarı: 204 -> null). 409: başkası claim etmiş / kapalı conversation.
// ---------------------------------------------------------------------------

export const claimConversation = conversationId =>
  call({ method: 'post', path: `${conv(conversationId)}/claim`, mock: 'workflow.claim' });

export const releaseConversation = (conversationId, reason) =>
  call({
    method: 'post',
    path: `${conv(conversationId)}/release`,
    data: reason ? { reason } : {},
    mock: 'workflow.release',
  });

/** Status anahtarla değil statusDefinitionId (guid) ile verilir; eşleme için getStatuses(). */
export const setConversationStatus = (conversationId, statusDefinitionId, reason) =>
  call({
    method: 'post',
    path: `${conv(conversationId)}/status`,
    data: reason ? { statusDefinitionId, reason } : { statusDefinitionId },
    mock: 'workflow.setStatus',
  });

/** reason zorunlu (en çok 500 karakter). */
export const reopenConversation = (conversationId, reason) =>
  call({
    method: 'post',
    path: `${conv(conversationId)}/reopen`,
    data: { reason },
    mock: 'workflow.reopen',
  });

export const getStatuses = () => call({ path: '/reference/statuses', mock: 'workflow.statuses' });

export const getReferenceQueues = () =>
  call({ path: '/reference/queues', mock: 'work.meQueues' });

export const getReferenceUsers = () =>
  call({ path: '/reference/users', mock: 'workflow.referenceUsers' });

// ---------------------------------------------------------------------------
// 2.6 Not ve görev
// ---------------------------------------------------------------------------

export const getNotes = conversationId =>
  call({ path: `${conv(conversationId)}/notes`, mock: 'notes.notes' });

export const addNote = (conversationId, body) =>
  call({
    method: 'post',
    path: `${conv(conversationId)}/notes`,
    data: { body },
    mock: 'notes.addNote',
  });

export const getConversationTasks = conversationId =>
  call({ path: `${conv(conversationId)}/tasks`, mock: 'notes.tasks' });

// payload: { title, assigneeUserId?, dueAtUtc?, checklist?: string[] }
export const createConversationTask = (conversationId, payload) =>
  call({
    method: 'post',
    path: `${conv(conversationId)}/tasks`,
    data: payload,
    mock: 'notes.createTask',
  });

export const getMyTasks = () => call({ path: '/me/tasks', mock: 'notes.myTasks' });

// ---------------------------------------------------------------------------
// 2.7 Agent durumu ve bildirim
// ---------------------------------------------------------------------------

export const getAvailability = () =>
  call({ path: '/me/availability', mock: 'agent.availability' });

/** status: Available | Away | Offline | OnLeave (OnLeave için confirmRedistribution: true gerekir). */
export const setAvailability = (status, confirmRedistribution = false) =>
  call({
    method: 'post',
    path: '/me/availability',
    data: { status, confirmRedistribution },
    mock: 'agent.setAvailability',
  });

// params: take, unreadOnly
export const getNotifications = (params = {}) =>
  call({ path: '/me/notifications', params, mock: 'agent.notifications' });

export const markNotificationRead = id =>
  call({
    method: 'post',
    path: `/me/notifications/${enc(id)}/read`,
    mock: 'agent.markNotificationRead',
  });

export const markAllNotificationsRead = () =>
  call({
    method: 'post',
    path: '/me/notifications/read-all',
    mock: 'agent.markAllNotificationsRead',
  });

// ---------------------------------------------------------------------------
// 2.8 Müşteri alanları (sınıflandırma). Mesaj kimliği = emailMessageMetadataId.
// field: FirstName | LastName | Address | ContactPhone. Değerler maskelidir; reveal audit'lenir.
// ---------------------------------------------------------------------------

export const getMessageClassification = emailMessageId =>
  call({
    path: `/messages/${enc(emailMessageId)}/classification`,
    mock: 'classification.summary',
  });

const fieldPath = (id, field, action) =>
  `/classification/customer-details/${enc(id)}/${enc(field)}/${action}`;

export const confirmCustomerField = (id, field) =>
  call({ method: 'post', path: fieldPath(id, field, 'confirm'), mock: 'classification.confirmField' });

export const correctCustomerField = (id, field, correctedValue) =>
  call({
    method: 'post',
    path: fieldPath(id, field, 'correct'),
    data: { correctedValue },
    mock: 'classification.correctField',
  });

export const rejectCustomerField = (id, field) =>
  call({ method: 'post', path: fieldPath(id, field, 'reject'), mock: 'classification.rejectField' });

export const revealCustomerField = (id, field) =>
  call({ method: 'post', path: fieldPath(id, field, 'reveal'), mock: 'classification.revealField' });

export const confirmProduct = id =>
  call({
    method: 'post',
    path: `/classification/products/${enc(id)}/confirm`,
    mock: 'classification.confirmProduct',
  });

export const correctProduct = (id, payload) =>
  call({
    method: 'post',
    path: `/classification/products/${enc(id)}/correct`,
    data: payload,
    mock: 'classification.correctProduct',
  });

export const rejectProduct = id =>
  call({
    method: 'post',
    path: `/classification/products/${enc(id)}/reject`,
    mock: 'classification.rejectProduct',
  });

export const setMessageCategory = (emailMessageId, payload) =>
  call({
    method: 'post',
    path: `/messages/${enc(emailMessageId)}/classification/category`,
    data: payload,
    mock: 'classification.setCategory',
  });

// ---------------------------------------------------------------------------
// İş emri oluşturma (SR-023/SR-024): SiriusApi POST api/MailOrbitWorkOrder/{conversationId}/Create
// -> { workOrderId, workCaseId, consumerId, created }. Zaten iş emri varsa mevcut olanı döner.
// ---------------------------------------------------------------------------

const MOCK_CREATED_WORK_ORDER = { workOrderId: 100001, workCaseId: 100001, consumerId: 100001, created: true };

/** GET api/MailOrbitWorkOrder/{conversationId} -> { conversationId, hasWorkOrder, workOrderId, workCaseId, isClosed }. Mock: bagli is emri yok. */
export const getWorkOrderLink = conversationId => {
  if (isMailOrbitMock()) {
    return Promise.resolve({
      conversationId,
      hasWorkOrder: false,
      workOrderId: null,
      workCaseId: null,
      isClosed: null,
    });
  }
  return send({ method: 'get', url: `/MailOrbitWorkOrder/${enc(conversationId)}` }).then(res => res.data);
};

/** newIfClosed: bagli is emri kapaliysa yeni is emri ac (?newIfClosed=true). */
export const createWorkOrderFromConversation = (conversationId, { newIfClosed = false } = {}) => {
  if (isMailOrbitMock()) return Promise.resolve({ ...MOCK_CREATED_WORK_ORDER });
  return send({
    method: 'post',
    url: `/MailOrbitWorkOrder/${enc(conversationId)}/Create`,
    params: newIfClosed ? { newIfClosed: true } : undefined,
  }).then(res => res.data);
};

// Mock: deterministik; kimligin karakter toplamina gore bos (0), acik (1) ya da kapali (2) is emri doner.
const mockLookupItem = conversationId => {
  const sum = String(conversationId)
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const kind = sum % 3;
  if (kind === 0) return null;
  return { conversationId, workOrderId: 100000 + (sum % 1000), isClosed: kind === 2 };
};

/**
 * POST api/MailOrbitWorkOrder/lookup (SR-045): body { conversationIds } -> { items: [{ conversationId, workOrderId, isClosed }] }.
 * Yalnizca bagli is emri olan conversation'lar items icinde doner. Liste sayfasi basina tek cagri (en fazla 100 kimlik).
 */
export const lookupWorkOrders = conversationIds => {
  const ids = conversationIds || [];
  if (isMailOrbitMock()) {
    return Promise.resolve({ items: ids.map(mockLookupItem).filter(Boolean) });
  }
  return send({
    method: 'post',
    url: '/MailOrbitWorkOrder/lookup',
    data: { conversationIds: ids },
  }).then(res => res.data);
};
